/**
 * EquipMoney: Woo cart/checkout *blocks* (React) + single-product qty for the price HTML widget (jQuery).
 *
 * PHP mirrors rules for the classic cart (`woocommerce_cart_subtotal`) and registers
 * `extensions.<STORE_API_NAMESPACE>.is_equipment` on Store API lines — see `include/equipmoney.php`.
 */

// --- config (keep in sync with `EquipMoney` in include/equipmoney.php) -----

const MIN_SUBTOTAL_MAJOR = 1000;

/** Must match `EquipMoney::STORE_API_NAMESPACE` (Store API + this file). */
const EXTENSION_NS = 'metro_studios';

const PLUGIN_ID = 'metro-equipmoney-order-meta';
const REGISTERED = '__metroEquipMoneyOrderMetaDone';

const POLL_MS = 50;
const POLL_TRIES = 200;

// --- cart helpers -----------------------------------------------------------

function cartItems(cart) {
  const raw = cart?.cartItems;
  return Array.isArray(raw) ? raw : [];
}

/**
 * Cart subtotal in major currency units (e.g. dollars), from block cart totals.
 */
function subtotalMajor(cart) {
  const totals = cart?.cartTotals;
  if (totals == null || totals.total_items == null) return 0;

  const minor = parseInt(String(totals.total_items), 10);
  if (Number.isNaN(minor)) return 0;

  const decimals = typeof totals.currency_minor_unit === 'number' ? totals.currency_minor_unit : 2;
  return minor / 10 ** decimals;
}

function lineIsEquipment(line) {
  return line?.extensions?.[EXTENSION_NS]?.is_equipment === true;
}

/**
 * When the cart qualifies for the widget, returns `{ items }`; otherwise `null`.
 * Single place for the PHP-matching rules (min subtotal + at least one equipment line).
 */
function eligibleCartOrNull(cart) {
  const items = cartItems(cart);
  if (items.length === 0) return null;
  if (subtotalMajor(cart) < MIN_SUBTOTAL_MAJOR) return null;
  if (!items.some(lineIsEquipment)) return null;
  return { items };
}

function describeCartLines(items) {
  return items
    .map((line) => {
      const sku = line.sku ? `${String(line.sku)}: ` : '';
      return `${sku}${String(line.name)} (${String(line.quantity)})`;
    })
    .join('\n');
}

// --- Woo blocks runtime -----------------------------------------------------

function wooBlocksCheckoutReady() {
  return Boolean(
    window.wp?.element?.createElement &&
    window.wp?.plugins?.registerPlugin &&
    window.wc?.blocksCheckout?.ExperimentalOrderMeta
  );
}

function registerEquipMoneySlot() {
  if (window[REGISTERED]) return true;
  if (!wooBlocksCheckoutReady()) return false;

  window[REGISTERED] = true;

  const createElement = window.wp.element.createElement;
  const { registerPlugin } = window.wp.plugins;
  const { ExperimentalOrderMeta } = window.wc.blocksCheckout;

  function EquipMoneySlot({ cart }) {
    const ok = eligibleCartOrNull(cart);
    if (!ok) return null;

    return createElement(
      'div',
      { className: 'metro-equipmoney-order-meta wc-block-components-totals-wrapper' },
      createElement('equipmoney-button', {
        'data-price': String(subtotalMajor(cart)),
        'data-product': describeCartLines(ok.items),
      })
    );
  }

  registerPlugin(PLUGIN_ID, {
    scope: 'woocommerce-checkout',
    render() {
      return createElement(ExperimentalOrderMeta, null, createElement(EquipMoneySlot, null));
    },
  });

  return true;
}

/**
 * Our bundle runs before `wc-blocks-checkout`; poll briefly until we can registerPlugin.
 */
function startWhenReady() {
  if (registerEquipMoneySlot()) return;

  let remaining = POLL_TRIES;
  const timer = setInterval(() => {
    remaining -= 1;
    if (registerEquipMoneySlot() || remaining <= 0) {
      clearInterval(timer);
    }
  }, POLL_MS);
}

startWhenReady();

// --- Single product: `data-qty` on `<equipmoney-button>` follows the qty input ----------------

function bindQuantityInputToEquipMoneyButton() {
  const jq = window.jQuery;
  if (typeof jq !== 'function') return;

  jq(document).on('change input', 'input.qty', function syncEquipMoneyQty() {
    const $button = jq(this).closest('.product').find('equipmoney-button').first();
    if ($button.length) {
      $button.attr('data-qty', this.value);
    }
  });
}

bindQuantityInputToEquipMoneyButton();
